import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './ClubFitting.css';
import RecommendationCards from "../components/RecommendationCards";
import { useUserProfile } from '../utils/useUserProfile';
import { useClubFitting } from '../utils/useClubFitting';
import ProfileSidebar from '../components/profile/ProfileSidebar';
import AIAnalysis from '../components/AIAnalysis';
import { DebugView } from '../components/debug/DebugView';
import ProfileModal from '../components/profile/ProfileModal';
import DistancesModal from '../components/profile/DistancesModal';
import AnalysisModal from '../components/profile/AnalysisModal';
import Messenger, { MessengerRef } from '../components/Messenger';
import TopBar from '../components/TopBar';
import { analyzeUserProfile } from '../utils/useUserProfile';
import { UserProfile } from '../utils/interfaces';
import { GOLFER_PRODUCT_RESULTS_CTAS, GOLFER_PRODUCT_RESULTS_CTA_INTERVAL_MS } from '../utils/golfFittingCtas';

export type ClubFittingRef = {
  openGeneralTab: () => void;
};

type ClubFittingProps = {
  onSignOut?: () => void;
};
const ClubFitting = forwardRef<ClubFittingRef, ClubFittingProps>(({ onSignOut }, ref) => {
  const navigate = useNavigate();
  
  const {
    userProfile,
    profileLoading,
    error: profileError,
    editableProfile,
    handleProfileChange,
    handleProfileSave,
    saveProfile,
    isLoggedIn,
    isNewProfile,
  } = useUserProfile();

  const {
    recommendations,
    loading: recsLoading,
    error: recsError,
    hasFetched,
    overview,
    compare,
    fetchRecommendations,
  } = useClubFitting();

  const [showDebugView, setShowDebugView] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDistancesModalOpen, setIsDistancesModalOpen] = useState(false);
  const [isAnalysisModalOpen, setIsAnalysisModalOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showAddClubTutorial, setShowAddClubTutorial] = useState(false);
  const messengerRef = useRef<MessengerRef>(null);
  const [analysis, setAnalysis] = useState<any | null>(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisRun, setAnalysisRun] = useState(false);
  const [loadingTextIndex, setLoadingTextIndex] = useState(0);
  const [lookupQuery, setLookupQuery] = useState('');
  const [fittingMode, setFittingMode] = useState<'basic' | 'advanced'>(() => {
    // Load fitting mode from localStorage or default to advanced
    const savedMode = localStorage.getItem('fittingMode') as 'basic' | 'advanced';
    return savedMode || 'advanced';
  });
  
  // Store the profile hash to detect actual profile changes
  const [profileHash, setProfileHash] = useState<string | null>(null);

  // Generate a hash of the user profile to detect actual changes
  const generateProfileHash = (profile: any) => {
    if (!profile) return null;
    return JSON.stringify(profile);
  };

  useEffect(() => {
    if (!recsLoading) {
      return;
    }

    const interval = setInterval(() => {
      setLoadingTextIndex((prevIndex) => (prevIndex + 1) % GOLFER_PRODUCT_RESULTS_CTAS.length);
    }, GOLFER_PRODUCT_RESULTS_CTA_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [recsLoading]);

  useEffect(() => {
    const cachedAnalysis = localStorage.getItem('profileAnalysis');
    const cachedProfileHash = localStorage.getItem('profileHash');
    
    if (cachedAnalysis && cachedProfileHash) {
      setAnalysis(JSON.parse(cachedAnalysis));
      setProfileHash(cachedProfileHash);
      setAnalysisRun(true);
    }
  }, []);

  useEffect(() => {
    const isMobileOrTouchDevice = /iPhone|iPad|iPod|Android|Mobile|Windows Phone/i.test(navigator.userAgent)
      || navigator.maxTouchPoints > 0
      || window.innerWidth < 1024;

    if (isMobileOrTouchDevice) {
      navigate('/club-fitting-mobile');
    }
  }, [navigate]);

  useEffect(() => {
    if (userProfile) {
      const currentProfileHash = generateProfileHash(userProfile);
      
      // Only clear analysis if the profile content has actually changed
      if (profileHash && currentProfileHash !== profileHash) {
        setAnalysisRun(false);
        setAnalysis(null);
        localStorage.removeItem('profileAnalysis');
        localStorage.removeItem('profileHash');
      }
      
      setProfileHash(currentProfileHash);
    }
  }, [userProfile, profileHash]);

  // Scroll to top and manage overflow for ClubFitting page
  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    // Restore overflow when ClubFitting unmounts
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, []);

  // Recommendation filter states
  const popularBrands = [
    'TaylorMade', 'Callaway', 'Titleist', 'Ping', 'Mizuno', 'Cobra', 'Cleveland', 
    'Wilson', 'Srixon', 'PXG', 'Honma', 'Bridgestone', 'Tour Edge', 'Yonex'
  ];
  
  const [selectedClub, setSelectedClub] = useState<string>('Full Set');
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [brandFieldTouched, setBrandFieldTouched] = useState<boolean>(false);
  const [showBrandDropdown, setShowBrandDropdown] = useState<boolean>(false);
  const [budget, setBudget] = useState<number>(500);
  const [selectedCondition, setSelectedCondition] = useState<'new' | 'used'>('new');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [lastSubmittedRequest, setLastSubmittedRequest] = useState<string | null>(null);

  const brandDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (brandDropdownRef.current && !brandDropdownRef.current.contains(event.target as Node)) {
        setShowBrandDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLookupClick = async () => {
    if (userProfile && lookupQuery) {
      const brandsToSend = brandFieldTouched && selectedBrands.length > 0 
        ? selectedBrands.join(', ') 
        : undefined;
      
      fetchRecommendations(
        userProfile, 
        isLoggedIn,
        selectedClub,
        brandsToSend,
        `$${budget}`,
        selectedCondition,
        lookupQuery
      );
    }
  };

  const handleAnalyzeProfile = async () => {
    if (userProfile) {
      setAnalysisLoading(true);
      setAnalysis(null);
      setIsAnalysisModalOpen(true);
      try {
        const result = await analyzeUserProfile(userProfile);
        const currentProfileHash = generateProfileHash(userProfile);
        
        setAnalysis(result);
        setProfileHash(currentProfileHash);
        
        // Cache both the analysis result and the profile hash
        localStorage.setItem('profileAnalysis', JSON.stringify(result));
        if (currentProfileHash) {
          localStorage.setItem('profileHash', currentProfileHash);
        }
        
        setAnalysisRun(true);
      } catch (error) {
        console.error("Failed to analyze profile:", error);
      } finally {
        setAnalysisLoading(false);
      }
    }
  };

  const handleGetRecsClick = () => {
    if (!userProfile) {
      return;
    }

    const brandsToSend = brandFieldTouched && selectedBrands.length > 0
      ? selectedBrands.join(', ')
      : undefined;

    const nextRequest = JSON.stringify({
      profile: userProfile,
      club: selectedClub,
      brands: brandsToSend,
      budget: `$${budget}`,
      condition: selectedCondition,
    });

    if (lastSubmittedRequest && nextRequest === lastSubmittedRequest) {
      setValidationError('Please update your player profile or search criteria before requesting new recommendations.');
      return;
    }

    setValidationError(null);
    setLastSubmittedRequest(nextRequest);

    fetchRecommendations(
      userProfile,
      isLoggedIn,
      selectedClub,
      brandsToSend,
      `$${budget}`,
      selectedCondition
    );
  };

  const handleBrandToggle = (brand: string) => {
    setBrandFieldTouched(true);
    setSelectedBrands(prev => 
      prev.includes(brand) 
        ? prev.filter(b => b !== brand)
        : [...prev, brand]
    );
  };

  const handleBrandFieldClick = () => {
    setBrandFieldTouched(true);
    setShowBrandDropdown(!showBrandDropdown);
  };

  const getBrandDisplayText = () => {
    if (!brandFieldTouched || selectedBrands.length === 0) {
      return 'All Brands';
    } else if (selectedBrands.length === 1) {
      return selectedBrands[0];
    } else {
      return `${selectedBrands.length} brands selected`;
    }
  };

  const handleSelectAllBrands = () => {
    setBrandFieldTouched(true);
    setSelectedBrands(popularBrands);
  };

  const handleDeselectAllBrands = () => {
    setBrandFieldTouched(true);
    setSelectedBrands([]);
  };

  const handleGetStarted = (skillLevel: 'beginner' | 'advanced') => {
    // Set fitting mode based on skill level choice
    if (skillLevel === 'beginner') {
      setFittingMode('basic');
      localStorage.setItem('fittingMode', 'basic');
      navigate('/basic-fitting');
    } else {
      setFittingMode('advanced');
      localStorage.setItem('fittingMode', 'advanced');
      navigate('/advanced-fitting');
    }
  };

  const handleNextTabClick = async (nextCategory: string) => {
    if (isLoggedIn) {
      await new Promise(resolve => setTimeout(resolve, 750)); // Wait 750 milliseconds
    } else {
      await new Promise(resolve => setTimeout(resolve, 200)); // Wait 200 milliseconds
    }
    handleTabClick(nextCategory);
  };

  const handleTabClick = (category: string) => {
    // Handle Distances tab in basic mode
    if (category === 'Distances' && fittingMode === 'basic') {
      setIsDistancesModalOpen(true);
      return;
    }

    setActiveCategory(category);
    setIsModalOpen(true);
    
    // Show Add Club tutorial for first-time club category opening
    const clubCategories = ['Fairway Woods', 'Utilities', 'Irons', 'Wedges'];
    if (clubCategories.includes(category) && !localStorage.getItem('add-club-tutorial-seen')) {
      // Small delay to let the modal open first
      setTimeout(() => {
        setShowAddClubTutorial(true);
      }, 300);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setActiveCategory(null);
    window.scrollTo(0, 0);
  };

  const handleSaveAndClose = async () => {
    try {
      await handleProfileSave();
      handleCloseModal();
    } catch (e) {
      console.error("Failed to save profile:", e);
      // Keep modal open on error, error is shown within the hook.
    }
  };

  const toggleDebugView = () => {
    setShowDebugView(!showDebugView);
  };

  const handleOpenMessenger = () => {
    messengerRef.current?.openMessenger();
  };

  const handleOpenAnalysisModal = () => {
    setIsAnalysisModalOpen(true);
  };

  const handleCloseAnalysisModal = () => {
    setIsAnalysisModalOpen(false);
  };

  // Check if this is first visit after completing questionnaire
  useEffect(() => {
    // Check if user has a profile (just completed questionnaire) but hasn't seen tutorial
    if (userProfile && !localStorage.getItem('tutorial-dismissed')) {
      // Small delay to let the page load
      const timer = setTimeout(() => {
        setShowTutorial(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [userProfile]);

  const handleDismissTutorial = () => {
    setShowTutorial(false);
    localStorage.setItem('tutorial-dismissed', 'true');
  };

  const handleDismissAddClubTutorial = () => {
    setShowAddClubTutorial(false);
    localStorage.setItem('add-club-tutorial-seen', 'true');
  };

  const handleModeChange = (mode: 'basic' | 'advanced') => {
    setFittingMode(mode);
    localStorage.setItem('fittingMode', mode);
  };

  const handleDistancesSave = async (distances: {driverDistance?: number, sevenIronDistance?: number, pitchingWedgeDistance?: number}) => {
    if (!editableProfile) {
      return;
    }

    try {
      const nextProfile: UserProfile = {
        ...editableProfile,
        driverDistance: distances.driverDistance ?? editableProfile.driverDistance,
        sevenIronDistance: distances.sevenIronDistance ?? editableProfile.sevenIronDistance,
        pitchingWedgeDistance: distances.pitchingWedgeDistance ?? editableProfile.pitchingWedgeDistance,
        Driver: {
          ...(editableProfile.Driver ?? { model: null, shaft: null, distance: null, notes: null }),
          distance: distances.driverDistance ?? editableProfile.Driver?.distance ?? null,
        },
        '7-Iron': {
          ...(editableProfile['7-Iron'] ?? { model: null, shaft: null, distance: null, notes: null }),
          distance: distances.sevenIronDistance ?? editableProfile['7-Iron']?.distance ?? null,
        },
        'Pitching Wedge': {
          ...(editableProfile['Pitching Wedge'] ?? { model: null, shaft: null, distance: null, notes: null }),
          distance: distances.pitchingWedgeDistance ?? editableProfile['Pitching Wedge']?.distance ?? null,
        },
      };

      await saveProfile(nextProfile);
    } catch (error) {
      console.error("Failed to save distances:", error);
    }
  };

  const handleCloseDistancesModal = () => {
    setIsDistancesModalOpen(false);
  };

  // Expose openGeneralTab to parent via ref
  useImperativeHandle(ref, () => ({
    openGeneralTab: () => handleTabClick('General'),
  }));

  const error = profileError || recsError;
  const loading = profileLoading || recsLoading;

  // Add this function before the return statement
  const handleEditProfile = () => {
    handleTabClick('General');
  };

  const handleOpenProfileModal = (category: string = 'General') => {
    handleTabClick(category);
  };

  const getProfileMenuCounts = (profile: UserProfile | null) => {
    const counts: Record<string, number> = {};
    if (!profile) return counts;

    const categories = ['Driver', 'Fairway Woods', 'Utilities', 'Irons', 'Wedges', 'Putter', 'Ball'] as const;

    categories.forEach((category) => {
      if (category === 'Driver') {
        counts[category] = profile.Driver ? 1 : 0;
      } else if (category === 'Fairway Woods') {
        const woodFields = ['3-Wood', '4-Wood', '5-Wood', '6-Wood', '7-Wood', '8-Wood', '9-Wood'];
        counts[category] = woodFields.filter((field) => {
          const value = profile[field as keyof UserProfile];
          return value !== undefined && value !== null && !(typeof value === 'string');
        }).length;
      } else if (category === 'Utilities') {
        const utilityFields = ['1-Iron', '2-Iron', '2-Hybrid', '3-Hybrid', '4-Hybrid', '5-Hybrid', '6-Hybrid'];
        counts[category] = utilityFields.filter((field) => {
          const value = profile[field as keyof UserProfile];
          return value !== undefined && value !== null && !(typeof value === 'string');
        }).length;
      } else if (category === 'Irons') {
        const ironFields = ['3-Iron', '4-Iron', '5-Iron', '6-Iron', '7-Iron', '8-Iron', '9-Iron', 'Pitching Wedge'];
        counts[category] = ironFields.filter((field) => {
          const value = profile[field as keyof UserProfile];
          return value !== undefined && value !== null && !(typeof value === 'string');
        }).length;
      } else if (category === 'Wedges') {
        const wedgeFields = ['Gap Wedge', 'Sand Wedge', 'Lob Wedge'];
        counts[category] = wedgeFields.filter((field) => {
          const value = profile[field as keyof UserProfile];
          return value !== undefined && value !== null && !(typeof value === 'string');
        }).length;
      } else if (category === 'Putter') {
        counts[category] = profile.Putter ? 1 : 0;
      } else if (category === 'Ball') {
        counts[category] = profile.Ball ? 1 : 0;
      }
    });

    return counts;
  };

  const profileMenuCounts = getProfileMenuCounts(userProfile as UserProfile | null);
  const shouldShowGetMyBagScoreAction = fittingMode === 'advanced' && !analysisRun;
  const shouldShowBagScoreAction = fittingMode === 'advanced' && analysisRun && Boolean(analysis);

  return (
    <>
      <TopBar
        showSignOut={isLoggedIn}
        onSignOut={onSignOut}
        onOpenProfileModal={handleOpenProfileModal}
        onOpenAnalysisModal={handleOpenAnalysisModal}
        onAnalyzeProfile={handleAnalyzeProfile}
        showGetMyBagScoreAction={shouldShowGetMyBagScoreAction}
        showBagScoreAction={shouldShowBagScoreAction}
        profileMenuCounts={profileMenuCounts}
      />
      {/* Tutorial Popup */}
      {showTutorial && !(typeof navigator !== 'undefined' && /iPhone|iPad|iPod/i.test(navigator.userAgent)) && (
        <div style={{
          position: 'fixed',
          top: '120px',
          left: '320px',
          zIndex: 9999
        }}>
          <div style={{
            position: 'relative',
            backgroundColor: '#fff',
            borderRadius: '8px',
            padding: '1.2rem',
            width: '280px',
            boxShadow: '0 6px 24px rgba(0, 0, 0, 0.15)',
            border: '2px solid #1976d2'
          }}>
            {/* Bigger arrow pointing to sidebar */}
            <div style={{
              position: 'absolute',
              left: '-30px',
              top: '30px',
              width: 0,
              height: 0,
              borderTop: '25px solid transparent',
              borderBottom: '25px solid transparent',
              borderRight: '30px solid #1976d2'
            }} />
      
            <div style={{
              textAlign: 'left'
            }}>
              <div style={{
                fontSize: '1.2rem',
                marginBottom: '0.8rem',
                color: '#1976d2',
                fontWeight: '600'
              }}>
                🎯 Fill out your bag!
              </div>
        
              <p style={{
                color: '#333',
                marginBottom: '1rem',
                fontSize: '0.9rem',
                lineHeight: '1.3',
                fontWeight: '500'
              }}>
                Continue filling out your bag for more precise recommendations
              </p>
        
              <p style={{
                color: '#666',
                marginBottom: '1rem',
                fontSize: '0.85rem',
                lineHeight: '1.3'
              }}>
                Click on club categories in your player profile to add details.
              </p>
        
              <button
                onClick={handleDismissTutorial}
                style={{
                  backgroundColor: '#1976d2',
                  color: '#fff',
                  border: 'none',
                  padding: '0.6rem 1.2rem',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease',
                  width: '100%'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#1565c0'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#1976d2'}
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="club-fitting-container">
        {userProfile && (
          <div className="profile-sidebar-desktop">
            <ProfileSidebar 
              onTabClick={handleTabClick}
              profileLoading={profileLoading}
              userProfile={userProfile}
              isLoggedIn={isLoggedIn}
              onAnalyzeProfile={handleAnalyzeProfile}
              analysisLoading={analysisLoading}
              analysisRun={analysisRun}
              analysis={analysis}
              onOpenAnalysisModal={handleOpenAnalysisModal}
              fittingMode={fittingMode}
              onModeChange={handleModeChange}
            />
          </div>
        )}

        <main className={`main-content ${!userProfile ? 'no-sidebar' : ''}`}>
          {profileLoading ? (
            <div className="loading-spinner">
              <div className="spinner"></div>
              <p>Loading your profile...</p>
            </div>
          ) : (
            <>
              {userProfile && !isNewProfile ? (
                <>
                  <div className="club-lookup-section" style={{ padding: '1rem 1rem', margin: '-0.5rem 0 2rem 0' }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem', 
                      marginBottom: '-0.75rem',
                      fontSize: '1.1rem',
                      fontWeight: 'bold'
                    }}>
                      <span style={{ color: '#d32f2f' }}>Ask NeutrAI</span>
                      <div 
                        style={{ 
                          position: 'relative',
                          display: 'inline-block',
                          cursor: 'help'
                        }}
                        onMouseEnter={(e) => {
                          const tooltip = e.currentTarget.querySelector('.custom-tooltip') as HTMLElement;
                          if (tooltip) tooltip.style.visibility = 'visible';
                        }}
                        onMouseLeave={(e) => {
                          const tooltip = e.currentTarget.querySelector('.custom-tooltip') as HTMLElement;
                          if (tooltip) tooltip.style.visibility = 'hidden';
                        }}
                      >
                        <div 
                          style={{
                            width: '14px',
                            height: '14px',
                            borderRadius: '50%',
                            border: '1px solid #666',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '8px',
                            fontWeight: 'bold',
                            color: '#666',
                            backgroundColor: 'white'
                          }}
                        >
                          ?
                        </div>
                        <div 
                          className="custom-tooltip"
                          style={{
                            visibility: 'hidden',
                            position: 'absolute',
                            top: '50%',
                            left: '100%',
                            transform: 'translateY(-50%)',
                            marginLeft: '8px',
                            backgroundColor: '#333',
                            color: 'white',
                            padding: '8px 12px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 'normal',
                            whiteSpace: 'nowrap',
                            zIndex: 1000,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                          }}
                        >
                          Try: "Titleist GT2 Driver" | "Can you give me a stiffer iron shaft?" | "Forgiving irons"
                          <div style={{
                            position: 'absolute',
                            top: '50%',
                            right: '100%',
                            transform: 'translateY(-50%)',
                            width: 0,
                            height: 0,
                            borderTop: '5px solid transparent',
                            borderBottom: '5px solid transparent',
                            borderRight: '5px solid #333'
                          }}></div>
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                      <input
                        type="text"
                        value={lookupQuery}
                        onChange={(e) => setLookupQuery(e.target.value)}
                        placeholder="Search for..."
                        className="filter-input"
                        style={{ flexGrow: 1 }}
                      />
                      <button
                        className="nf-btn nf-btn-primary"
                        onClick={handleLookupClick}
                        disabled={recsLoading || !lookupQuery}
                        style={{
                          whiteSpace: 'nowrap',
                          padding: '8px',
                          width: 'auto',           // let width fit content
                          minWidth: 'unset',       // remove any min width
                          maxWidth: 'unset',       // remove any max width
                          flexShrink: 0,           // prevent shrinking
                          flexGrow: 0,             // prevent growing
                          alignSelf: 'flex-start', // align with input
                        }}
                      >
                        {recsLoading ? (
                          <>
                            <span
                              className="nf-spinner"
                              style={{
                                width: 18,
                                height: 18,
                                border: '2.5px solid #fff',
                                borderTop: '2.5px solid #1976d2',
                                borderRadius: '50%',
                                display: 'inline-block',
                                animation: 'nf-spin 0.8s linear infinite',
                                marginRight: 8,
                              }}
                            />
                            Searching...
                          </>
                        ) : (
                          'Search'
                        )}
                      </button>
                    </div>
                  </div>
                  
                  <div className="recommendation-filters" style={{ padding: '1rem 1rem', margin: '0rem 0 0rem 0' }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem', 
                      marginBottom: '0.75rem',
                      fontSize: '1.1rem',
                      fontWeight: 'bold'
                    }}>
                      <span style={{ color: '#d32f2f' }}>NeutrAI Fitting</span>
                    </div>
                    <div className="filters-row-unified"
                      style={{
                        display: 'flex',
                        gap: '1rem',
                        alignItems: 'flex-end',
                        flexWrap: 'wrap'
                      }}
                    >
                      <div className="filter-section club-type-filter">
                        <label htmlFor="club-select">Search criteria</label>
                        <select 
                          id="club-select"
                          value={selectedClub} 
                          onChange={(e) => setSelectedClub(e.target.value)}
                          className="filter-select"
                        >
                          <option value="Full Set">Full Set</option>
                          <option value="Driver">Driver</option>
                          <option value="Fairway Wood">Fairway Wood</option>
                          <option value="Hybrid">Hybrid</option>
                          <option value="Irons">Irons</option>
                          <option value="Wedges">Wedges</option>
                          <option value="Putter">Putter</option>
                          <option value="Shafts">Shafts</option>
                          <option value="Ball">Ball</option>
                        </select>
                      </div>

                      <div className="filter-section brand-filter-section">
                        <label htmlFor="brand-select">Brand</label>
                        <div className="brand-select-container" ref={brandDropdownRef}>
                          <div 
                            className="brand-display-field"
                            onClick={handleBrandFieldClick}
                            tabIndex={0}
                          >
                            <span>{getBrandDisplayText()}</span>
                            <span className="brand-dropdown-arrow">▼</span>
                          </div>
                          {showBrandDropdown && (
                            <div className="brand-dropdown">
                              <div className="brand-controls">
                                <button className="brand-control-btn" onClick={handleSelectAllBrands}>Select All</button>
                                <button className="brand-control-btn" onClick={handleDeselectAllBrands}>Deselect All</button>
                              </div>
                              <div className="brand-options">
                                {popularBrands.map(brand => (
                                  <label key={brand} className="brand-option">
                                    <input 
                                      type="checkbox"
                                      checked={selectedBrands.includes(brand)}
                                      onChange={() => handleBrandToggle(brand)}
                                    />
                                    <span>{brand}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="filter-section">
                        <label htmlFor="budget-input">Budget</label>
                        <input 
                          id="budget-input"
                          type="number"
                          value={budget}
                          onChange={(e) => setBudget(Number(e.target.value))}
                          className="filter-input"
                          placeholder="$500"
                          min="0"
                          step="50"
                        />
                      </div>

                      <div className="filter-section">
                        <label>Condition</label>
                        <div className="condition-options">
                          <label className={`condition-radio ${selectedCondition === 'new' ? 'checked' : ''}`}>
                            <input 
                              type="radio" 
                              name="condition" 
                              value="new" 
                              checked={selectedCondition === 'new'} 
                              onChange={(e) => setSelectedCondition(e.target.value as 'new' | 'used')}
                            />
                            <span>New</span>
                          </label>
                          <label className={`condition-radio ${selectedCondition === 'used' ? 'checked' : ''}`}>
                            <input 
                              type="radio" 
                              name="condition" 
                              value="used" 
                              checked={selectedCondition === 'used'} 
                              onChange={(e) => setSelectedCondition(e.target.value as 'new' | 'used')}
                            />
                            <span>Used</span>
                          </label>
                        </div>
                      </div>
                      
                      <div style={{ display: 'flex', justifyContent: 'center', width: '100%', flexDirection: 'column', alignItems: 'center' }}>
                        <button
                          className="nf-btn nf-btn-primary"
                          onClick={handleGetRecsClick}
                          disabled={recsLoading}
                          style={{
                            whiteSpace: 'nowrap',
                            padding: '8px 16px',
                            width: 'auto',
                            minWidth: 'unset',
                            maxWidth: 'unset',
                            flexShrink: 0,
                            flexGrow: 0,
                            alignSelf: 'center',
                            backgroundColor: !recsLoading && hasFetched ? '#d32f2f' : undefined,
                            color: !recsLoading && hasFetched ? '#fff' : undefined,
                            transition: 'background 0.2s'
                          }}
                        >
                          {recsLoading ? (
                            <>
                              <span
                                className="nf-spinner"
                                style={{
                                  width: 18,
                                  height: 18,
                                  border: '2.5px solid #fff',
                                  borderTop: '2.5px solid #1976d2',
                                  borderRadius: '50%',
                                  display: 'inline-block',
                                  animation: 'nf-spin 0.8s linear infinite',
                                  marginRight: 8,
                                }}
                              />
                              Analyzing...
                            </>
                          ) : (
                            'Get Recommendations'
                          )}
                        </button>

                        {validationError && (
                          <div
                            className="error-message"
                            style={{
                              marginTop: '0.75rem',
                              width: '100%',
                              maxWidth: '100%',
                              color: '#dc2626',
                              fontWeight: 600,
                              padding: '0.75rem 1rem',
                              boxSizing: 'border-box',
                            }}
                          >
                            <p style={{ margin: 0, color: '#dc2626', width: '100%', maxWidth: '100%' }}>{validationError}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="profile-modal-container hide-on-iphone">
                    &nbsp;
                  </div>
                  
                  <div
                    style={{
                      position: 'fixed',
                      bottom: '-1rem',
                      right: '0.5rem',
                      zIndex: 1300,
                      width: 'min(400px, calc(100vw - 1rem))',
                      pointerEvents: 'auto'
                    }}
                  >
                    <Messenger ref={messengerRef} onEditProfile={handleEditProfile} />
                  </div>

                  {(() => {
                    const isIphone = typeof navigator !== 'undefined' && /iPhone|iPad|iPod/i.test(navigator.userAgent);
                    return (
                      <div style={isIphone ? { marginTop: '-1.60rem' } : {}}>
                        <AIAnalysis overview={overview} hasFetched={hasFetched} loading={recsLoading} onOpenMessenger={handleOpenMessenger} showLongTextToggle={false} />
                      </div>
                    );
                  })()}
                  
                  <div className="results-display-box">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                      {hasFetched || recommendations.length > 0 ? (
                        <h3 style={{ margin: 0 }}>Fitting Results</h3>
                      ) : null}
                    </div>
                    {error ? (
                      <div className="error-message" style={{ color: '#dc2626', fontWeight: 600 }}>
                        <p style={{ color: '#dc2626' }}>{error}</p>
                      </div>
                    ) : recsLoading ? (
                      <div className="loading-spinner" style={{ textAlign: 'center', padding: '3rem 0' }}>
                        <div className="spinner"></div>
                        <p style={{ marginTop: '0.75rem', color: '#000', fontWeight: 400 }}>
                          {GOLFER_PRODUCT_RESULTS_CTAS[loadingTextIndex]}
                        </p>
                      </div>
                    ) : recommendations.length === 0 && hasFetched ? (
                      <div className="no-results">
                        <p>No recommendations found. Please try again.</p>
                      </div>
                    ) : (
                      <RecommendationCards 
                        recommendations={recommendations}
                        compare={compare || undefined} 
                      />
                    )}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'center', margin: '2rem 0 4rem 0' }}>
                    <button
                      type="button"
                      onClick={toggleDebugView}
                      style={{
                        fontSize: '0.8rem',
                        padding: '0.5rem 1rem',
                        color: '#888',
                        backgroundColor: 'transparent',
                        border: 'none',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        fontWeight: 'normal'
                      }}
                    >
                      {showDebugView ? 'Hide Debug' : 'Show Debug'}
                    </button>
                  </div>
                  {showDebugView && userProfile && (
                    <DebugView userProfile={userProfile} />
                  )}
                </>
              ) : (
                <>
                  <div className="setup-wizard">
                    <h2 className="hide-on-iphone-p setup-title">Welcome to NeutralFit</h2>
                    <p className="hide-on-iphone-p setup-subtitle">
                      Get personalized golf club recommendations based on your profile and preferences.
                    </p>
                    <p className="setup-intro">Choose your path to get started:</p>
                    <div className="guidance-container setup-cards">
                      <div 
                        onClick={() => handleGetStarted('beginner')}
                        className="setup-card setup-card--basic"
                      >
                        <div className="setup-card-header">
                          <span className="setup-card-tag setup-card-tag--basic">Basics</span>
                        </div>
                        <h3 className="setup-card-title setup-card-title--basic">
                          Start with the Essentials
                        </h3>
                        <p className="setup-card-desc">
                          New to golf or not sure where to start? We’ll guide you through the essentials and build a solid baseline.
                        </p>
                        <ul className="setup-card-list">
                          <li>Handedness, skill level, and playing frequency</li>
                          <li>Basic measurements and preferences</li>
                          <li>Preferred brands (optional)</li>
                        </ul>
                        <p className="setup-card-note">
                          Perfect for beginners or casual players
                        </p>
                        <div className="setup-card-footer">
                          <span className="setup-card-cta">Start Basic Fitting</span>
                          <button type="button" className="setup-arrow" aria-label="Start with basics">
                            <span aria-hidden="true">→</span>
                          </button>
                        </div>
                      </div>

                      <div 
                        onClick={() => handleGetStarted('advanced')}
                        className="setup-card setup-card--advanced"
                      >
                        <div className="setup-card-header">
                          <span className="setup-card-tag setup-card-tag--advanced">Advanced</span>
                        </div>
                        <h3 className="setup-card-title setup-card-title--advanced">
                          Advanced Fitting Track
                        </h3>
                        <p className="setup-card-desc">
                          Have launch monitor data or know your numbers? Get a deeper, data-driven fitting.
                        </p>
                        <ul className="setup-card-list">
                          <li>Carry distance, launch angle, and spin rate</li>
                          <li>Current club models and specs</li>
                          <li>Swing/ball flight tendencies</li>
                          <li>Physical measurements (optional)</li>
                        </ul>
                        <p className="setup-card-note">
                          Best for players with launch data access
                        </p>
                        <div className="setup-card-footer">
                          <span className="setup-card-cta">Start Advanced Fitting</span>
                          <button type="button" className="setup-arrow" aria-label="Start advanced fitting">
                            <span aria-hidden="true">→</span>
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    <div className="setup-tip">
                      💡 <strong>Tip:</strong> You can always start simple and add more details later. 
                      The more information you provide, the better your recommendations will be!
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </main>
        <ProfileModal 
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          activeCategory={activeCategory}
          editableProfile={editableProfile}
          handleProfileChange={handleProfileChange}
          handleProfileSave={handleSaveAndClose}
          loading={loading}
          onContinueNextCategory={handleNextTabClick}
          showAddClubTutorial={showAddClubTutorial}
          onDismissAddClubTutorial={handleDismissAddClubTutorial}
        />

        <DistancesModal
          isOpen={isDistancesModalOpen}
          onClose={handleCloseDistancesModal}
          userProfile={userProfile}
          onSave={handleDistancesSave}
        />

        <AnalysisModal
          isOpen={isAnalysisModalOpen}
          onClose={handleCloseAnalysisModal}
          analysis={analysis}
          loading={analysisLoading}
        />
      </div>
    </>
  );
});

export default ClubFitting;
